/**
 * Game HUD - Handles progression celebration notifications
 * Shows level up, rank up, streak milestones, and achievement popups
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { usePlayerStatsStore, Achievement } from '../../state/playerStatsStore';

interface MilestoneNotification {
  id: string;
  title: string;
  subtitle: string;
  type: 'levelup' | 'rankup' | 'achievement' | 'dailybonus';
}

export const GameHUD: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const { stats, fetchStats } = usePlayerStatsStore();
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Helper to track timeouts for cleanup
  const trackTimeout = (callback: () => void, delay: number) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      callback();
    }, delay);
    timersRef.current.add(timer);
    return timer;
  };

  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(1);
  const [showRankUp, setShowRankUp] = useState(false);
  const [newRankName, setNewRankName] = useState('');
  const [milestones, setMilestones] = useState<MilestoneNotification[]>([]);

  // Fetch stats when wallet connects
  useEffect(() => {
    if (wallet) {
      fetchStats(wallet);
    }
  }, [wallet, fetchStats]);

  // Listen for progression events
  useEffect(() => {
    const handleLevelUp = (e: CustomEvent) => {
      setNewLevel(e.detail.level);
      setShowLevelUp(true);
      trackTimeout(() => setShowLevelUp(false), 3000);
    };

    const handleRankUp = (e: CustomEvent) => {
      setNewRankName(e.detail.rankName);
      setShowRankUp(true);
      const id = Math.random().toString();
      setMilestones((prev) => [
        ...prev,
        {
          id,
          title: `PROMOTED TO ${e.detail.rankName.toUpperCase()}!`,
          subtitle: 'Keep climbing the ranks!',
          type: 'rankup',
        },
      ]);
      trackTimeout(() => setShowRankUp(false), 4000);
      trackTimeout(() => setMilestones((prev) => prev.filter((m) => m.id !== id)), 5000);
    };

    const handleAchievement = (e: CustomEvent<Achievement>) => {
      const achievement = e.detail;
      const id = Math.random().toString();
      setMilestones((prev) => [
        ...prev,
        {
          id,
          title: achievement.name,
          subtitle: `+${achievement.xpReward} XP - ${achievement.description}`,
          type: 'achievement',
        },
      ]);
      trackTimeout(() => setMilestones((prev) => prev.filter((m) => m.id !== id)), 5000);
    };

    const handleDailyBonus = (e: CustomEvent) => {
      const { xpAwarded, loginStreak } = e.detail;
      const id = Math.random().toString();
      setMilestones((prev) => [
        ...prev,
        {
          id,
          title: 'DAILY BONUS!',
          subtitle: `+${xpAwarded} XP (Day ${loginStreak} streak)`,
          type: 'dailybonus',
        },
      ]);
      trackTimeout(() => setMilestones((prev) => prev.filter((m) => m.id !== id)), 4000);
    };

    window.addEventListener('progression:levelup', handleLevelUp as EventListener);
    window.addEventListener('progression:rankup', handleRankUp as EventListener);
    window.addEventListener('progression:achievement', handleAchievement as EventListener);
    window.addEventListener('progression:dailybonus', handleDailyBonus as EventListener);

    return () => {
      // Clear all pending timers
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();

      window.removeEventListener('progression:levelup', handleLevelUp as EventListener);
      window.removeEventListener('progression:rankup', handleRankUp as EventListener);
      window.removeEventListener('progression:achievement', handleAchievement as EventListener);
      window.removeEventListener('progression:dailybonus', handleDailyBonus as EventListener);
    };
  }, []);

  // Don't render if no wallet connected or no stats
  if (!wallet || !stats) {
    return null;
  }

  return (
    <>
      {/* Level Up Celebration */}
      <AnimatePresence>
        {showLevelUp && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div className="arcade-panel rounded-2xl p-8 text-center shadow-card">
              <div className="neon-text-pink font-display text-sm uppercase">Level Up!</div>
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, duration: 0.15, ease: 'easeOut' }}
                className="neon-text mt-2 font-display text-5xl"
              >
                {newLevel}
              </motion.div>
              <div className="mt-4 font-arcade text-sm text-white/60">
                Keep playing to climb the ranks!
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rank Up Celebration */}
      <AnimatePresence>
        {showRankUp && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div className="arcade-panel rounded-2xl border-arcade-pink/40 p-8 text-center shadow-card">
              <div className="neon-text font-display text-sm uppercase">Rank Up!</div>
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, duration: 0.15, ease: 'easeOut' }}
                className="neon-text-pink mt-2 font-display text-3xl"
              >
                {newRankName}
              </motion.div>
              <div className="mt-4 font-arcade text-sm text-white/60">
                You&apos;re climbing the leaderboard!
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Milestone Notifications */}
      <div className="fixed right-4 top-20 z-50 space-y-2">
        <AnimatePresence>
          {milestones.map((milestone) => (
            <motion.div
              key={milestone.id}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className={`arcade-panel rounded-lg p-4 shadow-card ${
                milestone.type === 'achievement'
                  ? 'border-l-2 border-l-yellow-400'
                  : milestone.type === 'rankup'
                    ? 'border-l-2 border-l-purple-400'
                    : milestone.type === 'dailybonus'
                      ? 'border-l-2 border-l-arcade-cyan'
                      : ''
              }`}
            >
              <div className="neon-text font-display text-base">{milestone.title}</div>
              <div className="mt-1 font-arcade text-xs text-white/60">{milestone.subtitle}</div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
};
